import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plane, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [inviteMode, setInviteMode] = useState(false);
  const [invite, setInvite] = useState(null);
  const [inviteToken, setInviteToken] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (token) {
      setInviteToken(token);
      setInviteMode(true);
      api.get(`/auth/invite/${token}`)
        .then((r) => setInvite(r.data))
        .catch(() => setError("Convite inválido ou já utilizado."));
    }
  }, [params]);

  useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (inviteMode) {
        const { data } = await api.post("/auth/register", { token: inviteToken, password });
        window.location.href = "/";
        void data;
      } else {
        await login(email, password);
        navigate("/", { replace: true });
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando…</div>;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="/aerogestor-logo.jpg"
          alt="AeroGestor"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/55 via-slate-900/30 to-blue-950/65" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/aerogestor-symbol.jpg" alt="" className="w-12 h-12 rounded-sm object-cover ring-1 ring-white/30" />
            <div>
              <div className="font-display font-black text-3xl tracking-tight ag-brand-metallic">AeroGestor</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mt-1">Hangar Operating System</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/70 mb-3">Plataforma Operacional</div>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05] tracking-tighter">
              Do pouso ao faturamento.<br/>
              <span className="text-sky-300">Rastreabilidade total</span> da rampa ao back-office.
            </h1>
            <p className="mt-6 text-white/80 max-w-md text-sm leading-relaxed">
              Ordens de serviço, checklist de intake, movimentações de aeronaves, assinatura digital e relatórios ANAC em um único sistema.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 text-[11px] tracking-[0.18em] uppercase text-white/70">
            <div><div className="text-white text-2xl font-display font-black">4</div>Estados</div>
            <div><div className="text-white text-2xl font-display font-black">3</div>Perfis</div>
            <div><div className="text-white text-2xl font-display font-black">ANAC</div>Pronto</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <img src="/aerogestor-symbol.jpg" alt="AeroGestor" className="w-10 h-10 rounded-sm object-cover ring-1 ring-slate-200" />
            <div className="font-display font-black text-2xl ag-brand-steel">AeroGestor</div>
          </div>

          <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">
            {inviteMode ? "Aceite seu Convite" : "Acesse o sistema"}
          </div>
          <h2 className="font-display font-black text-3xl tracking-tighter text-slate-950 mb-1">
            {inviteMode ? `Olá, ${invite?.name || "—"}` : "Entrar"}
          </h2>
          <p className="text-sm text-slate-600 mb-8">
            {inviteMode
              ? `Defina sua senha para acessar como ${invite?.role || "usuário"}.`
              : "Use suas credenciais corporativas."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {!inviteMode && (
              <div>
                <Label htmlFor="email" className="text-xs font-bold tracking-wider uppercase text-slate-600">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@empresa.com"
                  className="mt-1 h-12 rounded-sm"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
            )}
            <div>
              <Label htmlFor="password" className="text-xs font-bold tracking-wider uppercase text-slate-600">Senha</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 h-12 rounded-sm"
                data-testid="login-password-input"
                autoComplete={inviteMode ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm font-medium"
              data-testid="login-submit-button"
            >
              {submitting ? "Entrando…" : inviteMode ? "Criar senha & Entrar" : "Entrar"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
